import type { RollCallHistory } from '@cdf/api-client-react';

/**
 * A single exception: a student who was present at an earlier roll call but
 * absent or still pending at a later **started** roll call, without being
 * signed out before that later roll call.
 */
export interface RollCallException {
  studentId: string;
  /** Indexes into `history.rollCalls` where the regression was detected. */
  exceptionRollCallIndexes: number[];
}

/**
 * Determine which roll calls have "started" — i.e. at least one entry has been
 * marked (status is `present` or `absent`, not `pending`). A roll call where
 * every entry is still `pending` has not been conducted yet and should not
 * produce false exceptions.
 */
export function getStartedRollCallIndexes(history: RollCallHistory): Set<number> {
  const started = new Set<number>();
  const { rollCalls, students } = history;

  for (let rcIdx = 0; rcIdx < rollCalls.length; rcIdx++) {
    const hasMarked = students.some((s) => {
      const status = s.statuses[rcIdx];
      return status && (status.status === 'present' || status.status === 'absent');
    });
    if (hasMarked) started.add(rcIdx);
  }

  return started;
}

/**
 * Compute exceptions from the roll-call-history payload.
 *
 * A student is an exception when:
 * 1. They were `present` at an earlier roll call.
 * 2. At a **later** roll call that has started (≥1 marked entry), their status
 *    is `absent` or still `pending`.
 * 3. They were **not** signed out before that later roll call.
 *
 * The function returns one entry per offending student, listing every roll-call
 * index where the regression occurred.
 */
export function computeRollCallExceptions(history: RollCallHistory): RollCallException[] {
  const { rollCalls, students } = history;
  if (rollCalls.length < 2 || students.length === 0) return [];

  const startedIndexes = getStartedRollCallIndexes(history);
  const exceptions: RollCallException[] = [];

  for (const student of students) {
    const isSignedOut = student.checkinStatus === 'signed_out';
    const exceptionIndexes: number[] = [];

    // Track the first roll call where the student was present.
    let firstPresentIdx = -1;

    for (let rcIdx = 0; rcIdx < rollCalls.length; rcIdx++) {
      const status = student.statuses[rcIdx];
      if (!status) continue;

      if (status.status === 'present' && firstPresentIdx === -1) {
        firstPresentIdx = rcIdx;
        continue;
      }

      // Check for regression: present earlier, but absent or pending now.
      if (
        firstPresentIdx !== -1 &&
        firstPresentIdx < rcIdx &&
        startedIndexes.has(rcIdx) &&
        (status.status === 'absent' || status.status === 'pending')
      ) {
        // Exclude students signed out before this later roll call.
        // Since the payload only carries the current sign-out state (not the
        // timestamp), we exclude any signed-out student — their departure is
        // already explained by the sign-out column.
        if (!isSignedOut) {
          exceptionIndexes.push(rcIdx);
        }
      }
    }

    if (exceptionIndexes.length > 0) {
      exceptions.push({
        studentId: student.id,
        exceptionRollCallIndexes: exceptionIndexes
      });
    }
  }

  return exceptions;
}
